import type { RecoverySelection } from 'components/parking-agent/recoveryCsv'
/**
 * CSVSourceSelector - File type and source selection UI
 */

import { FileTypeSelector, FileType } from '../../FileTypeSelector'
import { FileSourceSelector, FileSource } from '../../FileSourceSelector'
import { LocalCSVUploader } from '../../LocalCSVUploader'
import { LocalDXFUploader } from '../../LocalDXFUploader'
import { ServerCSVList } from '../../ServerCSVList'
import { ServerCADList } from '../../ServerCADList'
import { LayerSelectionModal } from '../../LayerSelectionModal'
import { FileInfo } from '../../../../../models/FileStorage'
import { LayerInfo, SelectedLayerInfo } from '../../../../../services/DxfParserService'
import type { ViewMode } from '../types'
import styles from '../../CSVUploader.module.css'

interface CSVSourceSelectorProps {
  viewMode: ViewMode
  selectedFileType: FileType | null
  currentProjectId: number | null
  siteProjectId: number | null
  showLayerModal: boolean
  selectedDxfFile: FileInfo | null
  analyzedLayers: LayerInfo[]
  onFileTypeSelect: (type: FileType) => void
  onCancelFileTypeSelect: () => void
  onSourceSelect: (source: FileSource) => void
  onCancelSourceSelect: () => void
  onLocalFileSelect: (file: File) => Promise<void>
  onLocalDXFFileSelect: (file: File) => Promise<void>
  onServerCSVFileSelect: (file: FileInfo) => Promise<void>
  onCADFileSelect: (file: FileInfo) => Promise<void>
  onLayerSelectionConfirm: (layers: SelectedLayerInfo[], recovery?: RecoverySelection) => Promise<void>
  onLayerSelectionCancel: () => void
  onSetViewMode: (mode: ViewMode) => void
}

export function CSVSourceSelector({
  viewMode,
  selectedFileType,
  currentProjectId,
  siteProjectId,
  showLayerModal,
  selectedDxfFile,
  analyzedLayers,
  onFileTypeSelect,
  onCancelFileTypeSelect,
  onSourceSelect,
  onCancelSourceSelect,
  onLocalFileSelect,
  onLocalDXFFileSelect,
  onServerCSVFileSelect,
  onCADFileSelect,
  onLayerSelectionConfirm,
  onLayerSelectionCancel,
  onSetViewMode,
}: CSVSourceSelectorProps) {
  // Show file type selector view
  if (viewMode === 'file-type-select') {
    return <FileTypeSelector onTypeSelect={onFileTypeSelect} onCancel={onCancelFileTypeSelect} />
  }

  // Show source selector view
  if (viewMode === 'source-select') {
    if (!selectedFileType) {
      onSetViewMode('file-type-select')
      return null
    }

    return (
      <>
        <FileSourceSelector
          fileType={selectedFileType}
          onSourceSelect={onSourceSelect}
          onCancel={onCancelSourceSelect}
        />
        {showLayerModal && selectedDxfFile && (
          <LayerSelectionModal
            layers={analyzedLayers}
            isOpen={showLayerModal}
            onConfirm={onLayerSelectionConfirm}
            onCancel={onLayerSelectionCancel}
            filename={selectedDxfFile.original_name || selectedDxfFile.filename}
            sourceFilename={selectedDxfFile.filename}
            siteProjectId={siteProjectId}
          />
        )}
      </>
    )
  }

  // Show local CSV upload UI
  if (viewMode === 'local-upload') {
    return <LocalCSVUploader onFileSelect={onLocalFileSelect} onCancel={onCancelSourceSelect} />
  }

  // Show local DXF upload UI
  if (viewMode === 'local-dxf-upload') {
    return <LocalDXFUploader onFileSelect={onLocalDXFFileSelect} onCancel={onCancelSourceSelect} />
  }

  // Show server CSV list view
  if (viewMode === 'server-csv') {
    if (!siteProjectId) {
      return (
        <div className={styles.container}>
          <div className={`${styles.statusCard} ${styles.errorCard}`}>
            <div className={styles.statusText}>
              <p className={styles.statusTitle}>No Project Selected</p>
              <p className={styles.statusDescription}>Please select a project first</p>
            </div>
            <button onClick={() => onSetViewMode('idle')} className={styles.retryButton}>
              Back
            </button>
          </div>
        </div>
      )
    }

    return (
      <ServerCSVList
        projectId={siteProjectId}
        mapId={currentProjectId ?? undefined}
        onFileSelect={onServerCSVFileSelect}
        onCancel={onCancelSourceSelect}
      />
    )
  }

  // Show server CAD list view
  if (viewMode === 'server-cad') {
    if (!siteProjectId) {
      return (
        <div className={styles.container}>
          <div className={`${styles.statusCard} ${styles.errorCard}`}>
            <div className={styles.statusText}>
              <p className={styles.statusTitle}>No Project Selected</p>
              <p className={styles.statusDescription}>Please select a project first</p>
            </div>
            <button onClick={() => onSetViewMode('idle')} className={styles.retryButton}>
              Back
            </button>
          </div>
        </div>
      )
    }

    return (
      <ServerCADList
        projectId={siteProjectId}
        mapId={currentProjectId ?? undefined}
        onFileSelect={onCADFileSelect}
        onCancel={onCancelSourceSelect}
        onUploadClick={() => onSetViewMode('local-dxf-upload')}
      />
    )
  }

  return null
}
